import React, { useState, useEffect } from 'react';
import { TrendingUp, FileText, Target, Clock, Bell, TrendingDown, User, Shield, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { dashboardAPI, reportsAPI, liveAnnouncementsAPI, getErrorMessage } from '../../lib/api';

// Helper function to format date strings consistently (DD-MM-YYYY)
const formatDateString = (dateString) => {
  if (!dateString) return 'N/A';
  
  // If already in DD-MM-YYYY format, return as-is
  if (/^\d{2}-\d{2}-\d{4}/.test(dateString)) {
    return dateString.split(' ')[0];
  }
  
  // If in ISO format (YYYY-MM-DD or YYYY-MM-DDTHH:MM:SS), convert to DD-MM-YYYY
  if (/^\d{4}-\d{2}-\d{2}/.test(dateString)) {
    const [year, month, day] = dateString.split('T')[0].split('-');
    return `${day}-${month}-${year}`;
  }
  
  // Fallback: try to parse and format
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const DashboardHome = () => {
  // State for API data
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [reports, setReports] = useState([]);
  const [selectedStocks, setSelectedStocks] = useState([]);
  const [selectedIndices, setSelectedIndices] = useState([]);
  const [totalStocksCount, setTotalStocksCount] = useState(0);
  const [totalIndicesCount, setTotalIndicesCount] = useState(0);
  const [totalAccessibleStocks, setTotalAccessibleStocks] = useState(0);
  const [error, setError] = useState(null);

  // Fetch all data on mount
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch all data in parallel
      const [dashboardRes, reportsRes, selectionsRes] = await Promise.allSettled([
        dashboardAPI.getDashboard(),
        reportsAPI.getReports({ page_size: 4 }),
        liveAnnouncementsAPI.getUserSelections(),
      ]);

      // Process dashboard data
      if (dashboardRes.status === 'fulfilled') {
        setDashboardData(dashboardRes.value.data.data);
      }

      // Process reports - data is directly an array
      if (reportsRes.status === 'fulfilled') {
        const reportsData = reportsRes.value.data.data || reportsRes.value.data.results || [];
        setReports(Array.isArray(reportsData) ? reportsData.slice(0, 4) : []);
      }

      // Process user selections (stocks + indexes combined)
      if (selectionsRes.status === 'fulfilled') {
        const selectionsData = selectionsRes.value.data.data || selectionsRes.value.data || {};
        const stocksData = selectionsData.selected_stocks || [];
        const indexesData = selectionsData.selected_indexes || [];
        
        setTotalStocksCount(stocksData.length);
        setSelectedStocks(stocksData.slice(0, 5));
        setTotalIndicesCount(indexesData.length);
        setSelectedIndices(indexesData.slice(0, 5));
        setTotalAccessibleStocks(selectionsData.total_accessible_stocks || 0);
      }

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // Extract data from dashboard response
  const profile = dashboardData?.profile;
  const subscription = dashboardData?.subscription;
  const kycStatusCode = dashboardData?.kyc_status_code;
  const kycStatusText = dashboardData?.kyc_status_text;

  // Get subscription info
  const hasActiveSubscription = subscription?.subscription?.is_active;
  const hasActiveFreeTier = subscription?.free_tier?.is_active;
  const subscriptionPlan = subscription?.subscription?.plan_name || 'Free Tier';
  const subscriptionExpiry = subscription?.subscription?.expiry_date || subscription?.free_tier?.expiry_date;
  const daysRemaining = subscription?.subscription?.days_remaining || calculateDaysRemaining(subscriptionExpiry);

  // Calculate days remaining from expiry date
  function calculateDaysRemaining(expiryDate) {
    if (!expiryDate) return 0;
    const expiry = new Date(expiryDate);
    const today = new Date();
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  }

  // Get KYC status icon and colors
  const getKycStatusInfo = (statusCode) => {
    const statusMap = {
      0: { icon: XCircle, color: 'text-gray-500', bgColor: 'bg-gray-100', text: 'Not Submitted' },
      1: { icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-100', text: 'Verified' },
      2: { icon: Clock, color: 'text-blue-600', bgColor: 'bg-blue-100', text: 'Under Review' },
      3: { icon: XCircle, color: 'text-red-600', bgColor: 'bg-red-100', text: 'Rejected' },
    };
    return statusMap[statusCode] || statusMap[0];
  };

  const kycInfo = getKycStatusInfo(kycStatusCode);

  // Stats based on real data
  const stats = [
    {
      title: 'Selected Stocks',
      value: totalStocksCount.toString(),
      change: hasActiveSubscription ? 'Active' : 'Free Tier',
      positive: true,
      icon: <Target size={24} />
    },
    {
      title: 'Selected Indices',
      value: totalIndicesCount.toString(),
      change: hasActiveSubscription ? 'Active' : 'Free Tier',
      positive: true,
      icon: <TrendingUp size={24} />
    },
    {
      title: 'Total Stocks Tracked',
      value: totalAccessibleStocks.toString(),
      change: 'From selections',
      positive: true,
      icon: <TrendingDown size={24} />
    },
    {
      title: 'Reports',
      value: reports.length.toString(),
      change: 'Available',
      positive: true,
      icon: <FileText size={24} />
    },
    {
      title: 'Days Left',
      value: daysRemaining.toString(),
      change: hasActiveSubscription ? subscriptionPlan : 'Free Tier',
      positive: false,
      icon: <Clock size={24} />
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-[#1b2d4a] to-[#2a4a6f] text-white rounded-lg p-8">
        <h2 className="text-3xl font-bold mb-2">Welcome back, {profile?.user?.name?.split(' ')[0] || 'User'}!</h2>
        <p className="text-gray-300 text-lg">Here's what's happening with your investments today.</p>
      </div>

      {/* Subscription & KYC Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Subscription Info Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Shield className="text-blue-600" size={20} />
              Subscription Status
            </h3>
            <Link to="/dashboard/subscription" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
              Manage
            </Link>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Plan</span>
              <span className={`font-semibold ${hasActiveSubscription ? 'text-green-600' : 'text-blue-600'}`}>
                {hasActiveSubscription ? subscriptionPlan : 'Free Tier'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Status</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                hasActiveSubscription || hasActiveFreeTier 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-red-100 text-red-700'
              }`}>
                {hasActiveSubscription || hasActiveFreeTier ? 'Active' : 'Expired'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Expires</span>
              <span className="font-medium text-gray-900">{formatDateString(subscriptionExpiry)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Days Remaining</span>
              <span className={`font-bold text-xl ${daysRemaining <= 7 ? 'text-red-600' : 'text-green-600'}`}>
                {daysRemaining}
              </span>
            </div>
          </div>
        </div>

        {/* KYC Info Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <User className="text-blue-600" size={20} />
              KYC Verification
            </h3>
            <Link to="/dashboard/profile" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
              {kycStatusCode === 0 ? 'Complete KYC' : 'View Details'}
            </Link>
          </div>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-lg ${kycInfo.bgColor}`}>
                <kycInfo.icon className={kycInfo.color} size={24} />
              </div>
              <div>
                <p className="font-semibold text-gray-900">{kycStatusText || kycInfo.text}</p>
                <p className="text-sm text-gray-600">
                  {kycStatusCode === 1 && 'Your KYC is verified'}
                  {kycStatusCode === 2 && 'Your KYC is being reviewed'}
                  {kycStatusCode === 3 && 'Please resubmit your KYC'}
                  {kycStatusCode === 0 && 'Complete KYC to access all features'}
                </p>
              </div>
            </div>
            {profile?.pancard && (
              <div className="flex items-center justify-between pt-2 border-t">
                <span className="text-gray-600">PAN Card</span>
                <span className="font-medium text-gray-900">{profile.pancard}</span>
              </div>
            )}
            {profile?.whatsapp && (
              <div className="flex items-center justify-between">
                <span className="text-gray-600">WhatsApp</span>
                <span className="font-medium text-gray-900">{profile.whatsapp}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-lg ${
                stat.positive ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'
              }`}>
                {stat.icon}
              </div>
            </div>
            <h3 className="text-gray-600 text-sm font-medium mb-1">{stat.title}</h3>
            <p className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</p>
            <p className={`text-sm ${
              stat.positive ? 'text-green-600' : 'text-gray-500'
            }`}>{stat.change}</p>
          </div>
        ))}
      </div>

      {/* Performance Reports Section */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <FileText className="text-blue-600" size={20} />
              Performance Reports
            </h3>
            <Link to="/dashboard/reports" className="text-blue-600 hover:text-blue-800 text-sm font-semibold">
              View All
            </Link>
          </div>
        </div>
        <div className="p-6">
          {reports.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {reports.map((report, index) => (
                <Link 
                  key={report.id || index} 
                  to={`/dashboard/reports/${report.id}`}
                  className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <FileText className="text-yellow-600" size={20} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">{report.title || report.recommendation_stock || 'Report'}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-2 py-1 rounded ${
                        report.status === 'open' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {report.status || report.report_type || 'Report'}
                      </span>
                      <span className="text-xs text-gray-500">{formatDateString(report.recommendation_date || report.created_at)}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FileText size={48} className="mx-auto mb-3 text-gray-300" />
              <p className="text-sm">No reports available yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Selected Stocks & Indices Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Selected Stocks */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">
                My Selected Stocks
                {totalStocksCount > 0 && (
                  <span className="text-sm font-normal text-gray-500 ml-2">({totalStocksCount})</span>
                )}
              </h3>
              <Link to="/dashboard/stocks" className="text-blue-600 hover:text-blue-800 text-sm font-semibold">
                Manage
              </Link>
            </div>
          </div>
          <div className="p-6 space-y-3">
            {selectedStocks.length > 0 ? (
              <>
                {selectedStocks.map((stock, index) => (
                  <div key={stock.id || index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <h4 className="font-semibold text-gray-900">{stock.name}</h4>
                      <p className="text-sm text-gray-600">{stock.symbol}</p>
                    </div>
                    <div className="text-right">
                      {stock.current_price ? (
                        <>
                          <p className="font-semibold text-gray-900">₹{stock.current_price}</p>
                          <p className={`text-sm flex items-center gap-1 justify-end ${
                            (stock.change_percent || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {(stock.change_percent || 0) >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                            {(stock.change_percent || 0) >= 0 ? '+' : ''}{stock.change_percent || 0}%
                          </p>
                        </>
                      ) : (
                        <span className="text-xs text-gray-500">Selected</span>
                      )}
                    </div>
                  </div>
                ))}
                {totalStocksCount > 5 && (
                  <Link 
                    to="/dashboard/stocks" 
                    className="block text-center py-3 text-blue-600 hover:text-blue-800 font-semibold border-t mt-3"
                  >
                    View All {totalStocksCount} Stocks →
                  </Link>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Target size={48} className="mx-auto mb-3 text-gray-300" />
                <p className="text-sm">No stocks selected yet</p>
                <Link to="/dashboard/stocks" className="text-blue-600 hover:text-blue-800 text-sm font-semibold mt-2 inline-block">
                  Search Stocks
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Selected Indices */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">
                My Selected Indices
                {totalIndicesCount > 0 && (
                  <span className="text-sm font-normal text-gray-500 ml-2">({totalIndicesCount})</span>
                )}
              </h3>
              <Link to="/dashboard/index-search" className="text-blue-600 hover:text-blue-800 text-sm font-semibold">
                Manage
              </Link>
            </div>
          </div>
          <div className="p-6 space-y-3">
            {selectedIndices.length > 0 ? (
              <>
                {selectedIndices.map((index, idx) => (
                  <div key={index.id || idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <h4 className="font-semibold text-gray-900">{index.name}</h4>
                      {index.stock_count && (
                        <p className="text-sm text-gray-600">{index.stock_count} stocks</p>
                      )}
                    </div>
                    <div className="text-right">
                      {index.current_value ? (
                        <>
                          <p className="font-semibold text-gray-900">{index.current_value}</p>
                          <p className={`text-sm flex items-center gap-1 justify-end ${
                            (index.change_percent || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {(index.change_percent || 0) >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                            {(index.change_percent || 0) >= 0 ? '+' : ''}{index.change_percent || 0}%
                          </p>
                        </>
                      ) : (
                        <span className="text-xs text-gray-500">Selected</span>
                      )}
                    </div>
                  </div>
                ))}
                {totalIndicesCount > 5 && (
                  <Link 
                    to="/dashboard/index-search" 
                    className="block text-center py-3 text-blue-600 hover:text-blue-800 font-semibold border-t mt-3"
                  >
                    View All {totalIndicesCount} Indices →
                  </Link>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FileText size={48} className="mx-auto mb-3 text-gray-300" />
                <p className="text-sm">No indices selected yet</p>
                <Link to="/dashboard/index-search" className="text-blue-600 hover:text-blue-800 text-sm font-semibold mt-2 inline-block">
                  Search Indices
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link to="/dashboard/reports" className="p-4 border-2 border-gray-200 rounded-lg hover:border-yellow-400 hover:bg-yellow-50 transition-all text-center">
            <FileText className="mx-auto mb-2 text-gray-600" size={24} />
            <p className="text-sm font-semibold text-gray-900">View Reports</p>
          </Link>
          <Link to="/dashboard/stocks" className="p-4 border-2 border-gray-200 rounded-lg hover:border-yellow-400 hover:bg-yellow-50 transition-all text-center">
            <Target className="mx-auto mb-2 text-gray-600" size={24} />
            <p className="text-sm font-semibold text-gray-900">Stock Ideas</p>
          </Link>
          <Link to="/dashboard/profile" className="p-4 border-2 border-gray-200 rounded-lg hover:border-yellow-400 hover:bg-yellow-50 transition-all text-center">
            <User className="mx-auto mb-2 text-gray-600" size={24} />
            <p className="text-sm font-semibold text-gray-900">My Profile</p>
          </Link>
          <Link to="/dashboard/notifications" className="p-4 border-2 border-gray-200 rounded-lg hover:border-yellow-400 hover:bg-yellow-50 transition-all text-center">
            <Bell className="mx-auto mb-2 text-gray-600" size={24} />
            <p className="text-sm font-semibold text-gray-900">Notifications</p>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
